import React, { useState, useRef, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { Download, FileText, FileSpreadsheet, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';

/**
 * IngredientsExportButton — Export the ingredient master list as CSV / XLSX.
 *
 * - Respects current filtered set (default) OR exports all rows.
 * - Stable, Control-Room-compatible header order.
 * - Sorted alphabetically by ingredient_name (case-insensitive).
 * - CSV is UTF-8 with BOM so Excel opens it correctly.
 * - No internal/sensitive fields exported.
 */

const EXPORT_COLUMNS = [
  'ingredient_id',
  'ingredient_name',
  'canonical_name',
  'category',
  'sub_category',
  'base_unit',
  'status',
  'is_prep',
  'prep_id',
  'mapped_vendor_names',
  'last_purchase_date',
  'avg_cost',
  'current_stock',
  'needs_quantity_review',
  'unit_confidence',
  'mapping_status',
  'notes',
];

// Field-name patterns that should never appear in an export. Matched as full
// names or word-boundary tokens — not substrings — so legitimate fields like
// `ingredient_id` / `prep_id` are NEVER scrubbed.
const PII_EXACT = new Set([
  '_id', 'token', 'auth_token', 'service_token', 'secret', 'secret_key',
  'password', 'session_id',
]);
const PII_WORD_RE = /(?:^|_)(token|secret|password|session|auth)(?:_|$)/i;

const isSensitiveKey = (key) => {
  const k = String(key || '');
  if (PII_EXACT.has(k)) return true;
  // 'session_id' / 'auth_token' / 'foo_secret_bar' all match; 'ingredient_id' does NOT
  return PII_WORD_RE.test(k);
};

const safe = (v) => {
  if (v === null || v === undefined) return '';
  if (Array.isArray(v)) return v.filter((x) => x != null && x !== '').join('; ');
  if (typeof v === 'object') return ''; // never serialize nested objects to CSV
  return v;
};

const buildRow = (ing) => {
  // Defensive: never include any blacklisted field even if it sneaks in via spread
  const out = {};
  EXPORT_COLUMNS.forEach((col) => {
    let v;
    switch (col) {
      case 'ingredient_id':
        // Fall back across common id keys (CR canonical / Inventory / master)
        v = ing.ingredient_id ?? ing.canonical_ingredient_id ?? ing.id ?? ing._id ?? '';
        break;
      case 'ingredient_name':
        v = ing.ingredient_name ?? ing.name ?? '';
        break;
      case 'canonical_name':
        v = ing.canonical_name ?? ing.canonical_ingredient_name ?? '';
        break;
      case 'category':
        v = ing.category ?? ing.ingredient_category ?? '';
        break;
      case 'sub_category':
        v = ing.sub_category ?? ing.subcategory ?? '';
        break;
      case 'base_unit':
        v = ing.base_unit ?? ing.ingredient_base_unit ?? ing.base_uom ?? ing.unit ?? '';
        break;
      case 'status':
        v = ing.sync_status ?? ing.status ?? '';
        break;
      case 'is_prep':
        v = ing.is_prep ? 'true' : (ing.prep_id ? 'true' : '');
        break;
      case 'last_purchase_date':
        v = ing.last_price_date ?? ing.last_purchase_date ?? '';
        break;
      case 'current_stock':
        v = ing.soh ?? ing.current_stock ?? '';
        break;
      case 'avg_cost':
        v = ing.avg_cost ?? '';
        break;
      case 'mapped_vendor_names':
        v = ing.mapped_vendor_names ?? ing.vendor_names ?? '';
        break;
      case 'mapping_status':
        v = ing.mapping_status ?? ing.pricing_source ?? '';
        break;
      default:
        v = ing[col];
    }
    out[col] = safe(v);
  });
  // Final sweep: scrub anything that looks sensitive
  Object.keys(out).forEach((k) => {
    if (isSensitiveKey(k)) delete out[k];
  });
  return out;
};

const sortByName = (rows) =>
  [...rows].sort((a, b) =>
    String(a.ingredient_name || '').localeCompare(
      String(b.ingredient_name || ''),
      undefined,
      { sensitivity: 'base' }
    )
  );

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const stamp = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}-${pad(d.getHours())}${pad(d.getMinutes())}`;
};

const exportCsv = (rows) => {
  const header = EXPORT_COLUMNS.join(',');
  const escape = (v) => {
    const s = String(v ?? '');
    if (s.includes(',') || s.includes('"') || s.includes('\n')) {
      return `"${s.replace(/"/g, '""')}"`;
    }
    return s;
  };
  const body = rows
    .map((r) => EXPORT_COLUMNS.map((c) => escape(r[c])).join(','))
    .join('\n');
  // BOM so Excel opens UTF-8 correctly
  const csv = '\uFEFF' + header + '\n' + body;
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  downloadBlob(blob, `ingredients-${stamp()}.csv`);
};

const exportXlsx = (rows) => {
  const ws = XLSX.utils.json_to_sheet(rows, { header: EXPORT_COLUMNS });
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Ingredients');
  XLSX.writeFile(wb, `ingredients-${stamp()}.xlsx`);
};

const IngredientsExportButton = ({ ingredients = [], filteredIngredients = null }) => {
  const filtered = Array.isArray(filteredIngredients) ? filteredIngredients : ingredients;
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (!wrapRef.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const handleExport = (kind, scope) => {
    const source = scope === 'all' ? ingredients : filtered;
    if (!Array.isArray(source) || source.length === 0) {
      toast.error('Nothing to export');
      setOpen(false);
      return;
    }
    const rows = sortByName(source).map(buildRow);
    try {
      if (kind === 'csv') exportCsv(rows);
      else exportXlsx(rows);
      toast.success(`Exported ${rows.length} ingredient${rows.length === 1 ? '' : 's'}`);
    } catch (e) {
      toast.error(`Export failed: ${e?.message || 'unknown error'}`);
    }
    setOpen(false);
  };

  const filteredCount = Array.isArray(filtered) ? filtered.length : 0;
  const totalCount = Array.isArray(ingredients) ? ingredients.length : 0;
  const filterActive = filteredCount !== totalCount;

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        disabled={totalCount === 0}
        className="flex items-center gap-2 px-3 py-2 bg-[#27272a] hover:bg-[#3f3f46] border border-[#3f3f46] rounded-md text-sm font-medium disabled:opacity-50"
        data-testid="ingredients-export-btn"
      >
        <Download className="w-4 h-4" />
        Export
        <ChevronDown className="w-3.5 h-3.5 opacity-70" />
      </button>
      {open && (
        <div
          className="absolute right-0 mt-2 w-64 bg-[#0c0c0e] border border-[#3f3f46] rounded-md shadow-xl z-50"
          data-testid="ingredients-export-menu"
        >
          <div className="px-3 py-2 text-[10px] uppercase tracking-wider text-zinc-500 border-b border-[#27272a]">
            Filtered ({filteredCount.toLocaleString()})
          </div>
          <button
            type="button"
            onClick={() => handleExport('csv', 'filtered')}
            className="w-full text-left px-3 py-2 text-sm hover:bg-[#18181b] flex items-center gap-2"
            data-testid="ingredients-export-csv"
          >
            <FileText className="w-4 h-4 text-emerald-400" />
            Export CSV {filterActive && <span className="ml-auto text-[10px] text-zinc-500">filtered</span>}
          </button>
          <button
            type="button"
            onClick={() => handleExport('xlsx', 'filtered')}
            className="w-full text-left px-3 py-2 text-sm hover:bg-[#18181b] flex items-center gap-2"
            data-testid="ingredients-export-xlsx"
          >
            <FileSpreadsheet className="w-4 h-4 text-emerald-400" />
            Export Excel {filterActive && <span className="ml-auto text-[10px] text-zinc-500">filtered</span>}
          </button>
          {filterActive && (
            <>
              <div className="px-3 py-2 text-[10px] uppercase tracking-wider text-zinc-500 border-y border-[#27272a]">
                All ({totalCount.toLocaleString()})
              </div>
              <button
                type="button"
                onClick={() => handleExport('csv', 'all')}
                className="w-full text-left px-3 py-2 text-sm hover:bg-[#18181b] flex items-center gap-2"
                data-testid="ingredients-export-csv-all"
              >
                <FileText className="w-4 h-4 text-zinc-400" />
                Export CSV — all ingredients
              </button>
              <button
                type="button"
                onClick={() => handleExport('xlsx', 'all')}
                className="w-full text-left px-3 py-2 text-sm hover:bg-[#18181b] flex items-center gap-2"
                data-testid="ingredients-export-xlsx-all"
              >
                <FileSpreadsheet className="w-4 h-4 text-zinc-400" />
                Export Excel — all ingredients
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default IngredientsExportButton;
